<template>
    <v-card>
        <v-card-text>
            <v-form ref="form" v-model="valid" data-cy="products-mass-edit-price-form">
                <v-row>
                    <v-col cols="12" md="4">
                        <date-range-m-extended
                            :popover="{placement: 'bottom'}"
                            :show-icon="false"
                            :single-line="false"
                            :_start="model.dateFrom"
                            :_end="model.dateTo"
                            @change-start="model.dateFrom = $event"
                            @change-end="model.dateTo = $event"
                        />
                    </v-col>
                    <v-col cols="12" md="8">
                        <days-picker v-model="model.dayOfWeeks" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="2" class="d-flex flex-column align-center">
                        <v-text-field
                            v-model.number="model.minNights"
                            readonly
                            append-icon="mdi-plus"
                            prepend-icon="mdi-minus"
                            class="text-center shrink"
                            :rules="minRules"
                            hide-details
                            @click:append="incrementMinNights"
                            @click:prepend="decrementMinNights"
                        />
                        <span>{{ $t('supplier.min_nights') }}</span>
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex flex-column align-center">
                        <v-text-field
                            v-model.number="model.maxNights"
                            readonly
                            append-icon="mdi-plus"
                            prepend-icon="mdi-minus"
                            class="text-center shrink"
                            :rules="maxRules"
                            hide-details
                            @click:append="incrementMaxNights"
                            @click:prepend="decrementMaxNights"
                        />
                        <span>{{ $t('supplier.max_nights') }}</span>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="model.serviceId"
                            :label="$t('supplier.room_type')"
                            :items="groupedServices"
                            item-title="title"
                            item-value="value"
                            :rules="[requiredRule('supplier.room_type')]"
                            placeholder=""
                            :hint="$t('zsh.mass_edit_prices.basic_room_only')"
                            persistent-hint
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="model.tariffId"
                            :label="$t('tariff')"
                            :items="formattedTariffs"
                            item-title="displayName"
                            item-value="id"
                            :rules="[requiredRule('tariff')]"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            v-model.number="model.amount"
                            type="number"
                            :label="$t('price')"
                            :rules="[requiredRule('price')]"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn block color="primary" :disabled="!valid" @click="$emit('update', model)">
                            {{ $t('supplier.update') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, mixins, Prop, Watch, Setup} from 'vue-facing-decorator'
    import DateRangeMExtended from '~src/components/supplier/DateRangeMExtended.vue'
    import {FormValidationRulesMixin} from '~src/mixins/FormValidationRulesMixin.vue'
    import DaysPicker from '~src/components/supplier/DaysPicker.vue'
    import {supplierStore} from '~src/composables/store-accessor'
    import {useLocalized} from '~src/composables/localized'

    @Component({
        components: {DateRangeMExtended, DaysPicker},
    })
    export class MassEditPrices extends mixins(FormValidationRulesMixin) {
        @Prop() startDate
        @Prop() endDate
        @Prop() services
        @Prop() tariffs
        @Setup(() => useLocalized()) localized: Function

        model = {
            dateFrom: null,
            dateTo: null,
            dayOfWeeks: [true, true, true, true, true, true, true],
            minNights: null,
            maxNights: null,
            serviceId: null,
            tariffId: null,
            amount: null,
        }
        valid = false

        roomTypes = []
        hotelServices = []
        roomTypesLoaded = false
        hotelServicesLoaded = false

        async mounted() {
            this.model = {...this.model, dateFrom: this.startDate, dateTo: this.endDate}
            await this.loadHotelServices()
            await this.loadRoomTypeData()
            this.preselectBasicRoomType()
        }

        @Watch('services')
        async onServicesChange() {
            await this.loadHotelServices()
            await this.loadRoomTypeData()
            this.preselectBasicRoomType()
        }

        @Watch('roomTypesLoaded')
        @Watch('hotelServicesLoaded')
        onDataLoaded() {
            // Re-trigger preselection when data finishes loading
            if (this.roomTypesLoaded && this.hotelServicesLoaded) {
                this.preselectBasicRoomType()
            }
        }

        /**
         * Load hotel services to get serviceId to room type mapping
         * Uses the same approach as AvailabilityTableRows.vue
         */
        async loadHotelServices() {
            try {
                const contractId = supplierStore.contract?.id
                if (!contractId) {
                    // Mark as loaded even if no contract - fallback will show all services
                    this.hotelServicesLoaded = true
                    return
                }

                const response = await this.$api.ownHotelServices.get({contractId})
                this.hotelServices = response.services || []
            } catch (error) {
                this.hotelServices = []
            } finally {
                this.hotelServicesLoaded = true
            }
        }

        /**
         * Load room type data with sustainability levels
         */
        async loadRoomTypeData() {
            try {
                // Get hotel ID from supplier store
                const hotelId = supplierStore.hotelInfo?.hotelId
                if (!hotelId) {
                    // Mark as loaded even if no hotel - fallback will show all services
                    this.roomTypesLoaded = true
                    return
                }

                const {hotelRoomTypes} = await this.$api.hotelDescriptionRoomType.get(hotelId, {
                    locale: 'all',
                })

                // Process room types to ensure they have the required sustainability level
                this.roomTypes = hotelRoomTypes.map(roomType => ({
                    ...roomType,
                    sustainabilityLevel: roomType.sustainabilityLevel || 'BASIC',
                    basicRoomId: roomType.basicRoomId || null,
                }))
            } catch (e) {
                this.roomTypes = []
            } finally {
                this.roomTypesLoaded = true
            }
        }

        /**
         * Get room type by service ID using the same logic as AvailabilityTableRows.vue
         */
        getRoomTypeByServiceId(serviceId) {
            // First try to find the service in hotel services to get the room type ID
            const hotelService = this.hotelServices.find(service => service.id === serviceId)
            if (hotelService && hotelService.hotelRoomTypeId) {
                // Use the hotelRoomTypeId from hotel service to find the room type
                const roomType = this.roomTypes.find(rt => rt.id === hotelService.hotelRoomTypeId)
                if (roomType) {
                    return roomType
                }
            }

            // Fallback: try direct serviceId match with room type ID
            const directMatch = this.roomTypes.find(rt => rt.id === serviceId)
            if (directMatch) {
                return directMatch
            }

            return null
        }

        /**
         * Filter services to only show BASIC room types
         * Uses the same approach as AvailabilityTableRows.vue
         */
        get filteredServices() {
            if (!this.services || this.services.length === 0) {
                return []
            }

            // If we have both hotel services and room type data loaded, filter by sustainability level
            if (
                this.roomTypesLoaded &&
                this.hotelServicesLoaded &&
                this.hotelServices.length > 0 &&
                this.roomTypes.length > 0
            ) {
                const filtered = this.services.filter(service => {
                    const roomType = this.getRoomTypeByServiceId(service.id)

                    if (roomType) {
                        const sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'
                        // Only include BASIC room types
                        return sustainabilityLevel === 'BASIC'
                    }

                    // If no room type found, exclude it (strict filtering for ZSH)
                    return false
                })

                return filtered
            }

            // Fallback: if data not loaded yet, return all services to populate dropdown
            return this.services
        }

        /**
         * Create grouped services for the dropdown, showing only BASIC room types without duplicates.
         * Built from hotelServices + roomTypes so that newly created room types appear
         * even before they have daily rates configured.
         */
        get groupedServices() {
            if (!this.roomTypesLoaded || !this.hotelServicesLoaded) {
                return []
            }

            const groupedItems = []
            const seenNames = new Set()

            for (const hotelService of this.hotelServices) {
                const roomType = this.roomTypes.find(rt => rt.id === hotelService.hotelRoomTypeId)
                if (!roomType) continue
                if (roomType.active === false) continue // ZSH-873: hide disabled room type

                const sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'
                if (sustainabilityLevel !== 'BASIC') continue

                const name = this.localized(roomType.name) || `Room ${hotelService.id}`
                if (seenNames.has(name)) continue

                seenNames.add(name)
                groupedItems.push({
                    title: name,
                    value: hotelService.id,
                })
            }

            return groupedItems.sort((a, b) => a.title.localeCompare(b.title))
        }

        /**
         * Format tariffs for dropdown, handling localized names
         */
        get formattedTariffs() {
            if (!this.tariffs || this.tariffs.length === 0) {
                return []
            }

            return this.tariffs.map(tariff => ({
                id: tariff.id,
                displayName: this.localized(tariff.name) || `Tariff ${tariff.id}`,
                ...tariff,
            }))
        }

        /**
         * Pre-select the first available BASIC room type
         */
        preselectBasicRoomType() {
            if (!this.groupedServices || this.groupedServices.length === 0) {
                this.model.serviceId = null
                return
            }

            // Auto-select the first BASIC room type if none is selected or if current selection is not valid
            const currentSelection = this.groupedServices.find(item => item.value === this.model.serviceId)
            if (!this.model.serviceId || !currentSelection) {
                this.model.serviceId = this.groupedServices[0].value
            }
        }

        incrementMinNights() {
            this.model.minNights = (parseInt(this.model.minNights) || 0) + 1
        }

        decrementMinNights() {
            this.model.minNights = (parseInt(this.model.minNights) || 0) - 1
            if (this.model.minNights < 0) {
                this.model.minNights = 0
            }
        }

        incrementMaxNights() {
            this.model.maxNights = (parseInt(this.model.maxNights) || 0) + 1
        }

        decrementMaxNights() {
            this.model.maxNights = (parseInt(this.model.maxNights) || 0) - 1
            if (this.model.maxNights < 0) {
                this.model.maxNights = 0
            }
        }

        get minRules() {
            return [
                v =>
                    !this.model.maxNights ||
                    this.model.maxNights >= v ||
                    `${this.$t('supplier.invalid_number_interval')}`,
            ]
        }

        get maxRules() {
            return [
                v =>
                    !this.model.minNights ||
                    this.model.minNights <= v ||
                    `${this.$t('supplier.invalid_number_interval')}`,
            ]
        }
    }

    export default MassEditPrices
</script>

<style scoped lang="scss">
    :deep(.v-text-field.text-center input) {
        text-align: center;
    }

    // ZSH theme styling for room type select
    // Target .v-field__outline (fieldset) instead of .v-field to preserve
    // the legend notch that creates a gap for the floating label.
    // Adding a border on .v-field directly causes a "strike-through" line
    // through the label text because .v-field has no notch mechanism.
    :deep(.v-select) {
        .v-field__outline {
            border-color: var(--zsh-primary, #3699a4);
        }

        &:hover .v-field__outline {
            border-color: var(--zsh-primary-dark, #2a7a84);
        }
    }
</style>
